import React, {useEffect, useState} from 'react';
import {CustomModal} from "../../../../../components/UI/AntCustom/CustomModal";
import {CustomButton} from "../../../../../components/UI/AntCustom/CustomButton";
import {useIntl, defineMessages} from "react-intl";
import {useAppDispatch, useAppSelector} from "../../../../../store/store";
import {
  cancelSubscription,
  resumeSubscription,
  clearCancelSubscription,
  clearResumeSubscription,
  updateSubscriptionQuantity,
  clearUpdateSubscriptionQuantity,
} from "../../../../../store/reducers/tariffReducer/tariffReducer";
import styled from "styled-components";
import {Alert, InputNumber} from "antd";
import {ITariffPackage} from "../../../../../store/reducers/tariffReducer/tariffReducerTypes";
import dayjs from "dayjs";
import {PaymentMethodModal} from "./PaymentMethodModal";
import {BillingApi} from "../../../../../API/ApiClasses/BillingApi";
import {BillingApiGetPaymentMethodsRequest} from "../../../../../proto/generated/api_entities_pb";

const ManageSubscriptionModalMessages = defineMessages({
  title: {
    id: "manageSubscriptionModal.title",
    defaultMessage: "Manage Subscription",
  },
  cancelSubscription: {
    id: "manageSubscriptionModal.cancelSubscription",
    defaultMessage: "Cancel subscription",
  },
  renewSubscription: {
    id: "manageSubscriptionModal.renewSubscription",
    defaultMessage: "Renew Subscription",
  },
  startSubscription: {
    id: "manageSubscriptionModal.startSubscription",
    defaultMessage: "Start Subscription",
  },
  cancelConfirm: {
    id: "manageSubscriptionModal.cancelConfirm",
    defaultMessage: "Are you sure you want to cancel your subscription? You will retain access until the end of the billing period.",
  },
  billingInfo: {
    id: "manageSubscriptionModal.billingInfo",
    defaultMessage: "Billing",
  },
  nextBillingDate: {
    id: "manageSubscriptionModal.nextBillingDate",
    defaultMessage: "Next billing date",
  },
  projectedAmount: {
    id: "manageSubscriptionModal.projectedAmount",
    defaultMessage: "Projected amount",
  },
  projectedAmountProrated: {
    id: "manageSubscriptionModal.projectedAmountProrated",
    defaultMessage: "{amount} until {date}, then {monthlyAmount}/month",
  },
  projectedAmountFull: {
    id: "manageSubscriptionModal.projectedAmountFull",
    defaultMessage: "{amount}/month",
  },
  projectedAmountWithDiscount: {
    id: "manageSubscriptionModal.projectedAmountWithDiscount",
    defaultMessage: "{amount}/month for the first {months} months, then {fullAmount}/month",
  },
  discountUntilDate: {
    id: "manageSubscriptionModal.discountUntilDate",
    defaultMessage: "{percent}% off until {date}, then {fullAmount}/month",
  },
  projectedAmountProratedWithDiscount: {
    id: "manageSubscriptionModal.projectedAmountProratedWithDiscount",
    defaultMessage: "{amount} until {date}, then {discountedAmount}/month for the first {months} months, then {fullAmount}/month",
  },
  cancelMessage: {
    id: "manageSubscriptionModal.cancelMessage",
    defaultMessage: "If you cancel, you will retain access until {date}. Your subscription will not renew after that date.",
  },
  cancelDiscountWarning: {
    id: "manageSubscriptionModal.cancelDiscountWarning",
    defaultMessage: "If you cancel, this promotional discount will not apply when you resubscribe.",
  },
  renewMessage: {
    id: "manageSubscriptionModal.renewMessage",
    defaultMessage: "Subscription will start from {date}. You will be charged {amount}.",
  },
  addCardFirst: {
    id: "manageSubscriptionModal.addCardFirst",
    defaultMessage: "Please add a payment method before activating the subscription.",
  },
  discountPromo: {
    id: "manageSubscriptionModal.discountPromo",
    defaultMessage: "{percent}% off for the first {months} months",
  },
  seatsLabel: {
    id: "manageSubscriptionModal.seatsLabel",
    defaultMessage: "Seats (users)",
  },
  aircraftLabel: {
    id: "manageSubscriptionModal.aircraftLabel",
    defaultMessage: "Aircraft slots",
  },
});

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 24px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 12px;
`;

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #000000;
`;

function formatMoney(cents: number, currency: string): string {
  const code = currency?.toUpperCase() || 'USD';
  if (code === 'USD') {
    return `$${(cents / 100).toFixed(2)}`;
  }
  return new Intl.NumberFormat(undefined, {style: 'currency', currency: code}).format(cents / 100);
}

interface IManageSubscriptionModal {
  open: boolean;
  onClose: () => void;
  activePackage: ITariffPackage | null;
  subscriptionPrices?: {
    pricePerSeatCents?: number;
    pricePerAircraftCents?: number;
    currency?: string;
    discountPercent?: number;
    discountDurationMonths?: number;
  };
  activeUsers?: number;
  activeAircraft?: number;
  isOperator?: boolean;
  onSuccess?: () => void;
}

export const ManageSubscriptionModal: React.FC<IManageSubscriptionModal> = ({
  open,
  onClose,
  activePackage,
  subscriptionPrices,
  activeUsers = 0,
  activeAircraft = 0,
  isOperator = false,
  onSuccess,
}) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const cancelSubscriptionState = useAppSelector(state => state.tariff.cancelSubscription);
  const resumeSubscriptionState = useAppSelector(state => state.tariff.resumeSubscription);
  const updateQuantityState = useAppSelector(state => state.tariff.updateSubscriptionQuantity);
  const [error, setError] = useState<string | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<{id: string}[]>([]);
  const [isPaymentMethodModalVisible, setIsPaymentMethodModalVisible] = useState(false);
  const [pendingResumeAfterCardAdd, setPendingResumeAfterCardAdd] = useState(false);
  const [pendingQuantityUpdateAfterResume, setPendingQuantityUpdateAfterResume] = useState(false);

  const subscribedUsers = activePackage?.subscribedUsersCount ?? activeUsers;
  const subscribedAircraft = activePackage?.subscribedAircraftCount ?? activeAircraft;
  const billedUsers = activePackage?.billedUsersCount ?? activeUsers;
  const billedAircraft = activePackage?.billedAircraftCount ?? activeAircraft;

  // Display subscribed counts (what they bought); min = billed (can't reduce below usage)
  // Operators can have 0 seats (no managers); brokers always have at least 1 (admin)
  const minUsers = isOperator ? billedUsers : Math.max(billedUsers, 1);
  const [usersQuantity, setUsersQuantity] = useState(subscribedUsers || minUsers);
  const [aircraftQuantity, setAircraftQuantity] = useState(Math.max(subscribedAircraft, billedAircraft, 0));

  useEffect(() => {
    if (open && activePackage) {
      const subUsers = activePackage.subscribedUsersCount ?? activeUsers;
      const subAircraft = activePackage.subscribedAircraftCount ?? activeAircraft;
      const billUsers = activePackage.billedUsersCount ?? activeUsers;
      const billAircraft = activePackage.billedAircraftCount ?? activeAircraft;
      const minU = isOperator ? billUsers : Math.max(billUsers, 1);
      setUsersQuantity(subUsers || minU);
      setAircraftQuantity(Math.max(subAircraft, billAircraft, 0));
    }
  }, [open, activePackage?.companyTariffPackageId, activePackage?.subscribedUsersCount, activePackage?.subscribedAircraftCount, activePackage?.billedUsersCount, activePackage?.billedAircraftCount, activeUsers, activeAircraft, isOperator]);

  // Fetch payment methods when modal opens
  useEffect(() => {
    if (open) {
      setError(null);
      const fetchPaymentMethods = async () => {
        try {
          const req = new BillingApiGetPaymentMethodsRequest();
          const res = await BillingApi.getPaymentMethods(req);
          if (res.getResultresponse()?.getIssuccessful()) {
            const methods = res.getPaymentmethodsList().map(pm => ({id: pm.getPaymentmethodid()}));
            setPaymentMethods(methods);
          } else {
            setPaymentMethods([]);
          }
        } catch {
          setPaymentMethods([]);
        }
      };
      fetchPaymentMethods();
    }
  }, [open]);

  // Show error when resume or updateQuantity fails
  useEffect(() => {
    if (resumeSubscriptionState.errorMessage || updateQuantityState.errorMessage) {
      setError(resumeSubscriptionState.errorMessage || updateQuantityState.errorMessage || null);
    }
  }, [resumeSubscriptionState.errorMessage, updateQuantityState.errorMessage]);

  useEffect(() => {
    if (cancelSubscriptionState.isSuccess) {
      dispatch(clearCancelSubscription());
      onSuccess?.();
      onClose();
    }
  }, [cancelSubscriptionState.isSuccess, dispatch, onSuccess, onClose]);

  useEffect(() => {
    if (resumeSubscriptionState.isSuccess) {
      dispatch(clearResumeSubscription());
      setPendingResumeAfterCardAdd(false);

      const needsQuantityUpdate = activePackage?.companyTariffPackageId &&
        (usersQuantity !== subscribedUsers || (isOperator && aircraftQuantity !== subscribedAircraft));

      if (needsQuantityUpdate) {
        setPendingQuantityUpdateAfterResume(true);
        dispatch(updateSubscriptionQuantity({
          companyTariffPackageId: activePackage.companyTariffPackageId,
          usersQuantity,
          aircraftQuantity: isOperator ? aircraftQuantity : 0,
        }));
      } else {
        onSuccess?.();
        onClose();
      }
    }
  }, [resumeSubscriptionState.isSuccess, dispatch, onSuccess, onClose, activePackage?.companyTariffPackageId, usersQuantity, aircraftQuantity, subscribedUsers, subscribedAircraft, isOperator]);

  useEffect(() => {
    if (updateQuantityState.isSuccess && pendingQuantityUpdateAfterResume) {
      dispatch(clearUpdateSubscriptionQuantity());
      setPendingQuantityUpdateAfterResume(false);
      onSuccess?.();
      onClose();
    }
  }, [updateQuantityState.isSuccess, pendingQuantityUpdateAfterResume, dispatch, onSuccess, onClose]);

  useEffect(() => {
    if (updateQuantityState.errorMessage && pendingQuantityUpdateAfterResume) {
      setPendingQuantityUpdateAfterResume(false);
    }
  }, [updateQuantityState.errorMessage, pendingQuantityUpdateAfterResume]);

  const cancelResumeLoading = cancelSubscriptionState.isLoading || resumeSubscriptionState.isLoading || updateQuantityState.isLoading;

  const subscriptionStatus = activePackage?.subscriptionStatus;
  const isCanceled = subscriptionStatus === 'canceled';
  const autoRenewal = activePackage?.autoRenewal ?? false;
  // Never had a Stripe subscription before (e.g. trial ended, not_started) → "Start"; had one and canceled → "Renew"
  const neverHadSubscription = !activePackage?.stripeSubscriptionId || subscriptionStatus === 'not_started';

  // Subscription end date (valid until) - when access ends if canceled. Prefer Stripe current_period_end.
  const subscriptionEndDate =
    activePackage?.currentPeriodEnd || activePackage?.tariffEnd || activePackage?.trialEndAt;

  // Next billing date: when the next charge will occur.
  // For active subscription: current period end (when next charge happens).
  // For canceled (access still valid): start of next period = period end.
  // For canceled (access ended): today when user clicks Renew.
  const nextBillingDate = (() => {
    if (!activePackage) return null;
    const anchor = activePackage.billingCycleAnchor;
    const trialEnd = activePackage.trialEndAt;
    if (trialEnd && dayjs(trialEnd).isAfter(dayjs())) {
      // On trial: next billing is when trial ends (first charge). Prefer trial end over anchor
      // (anchor may be 1st of next month; actual first charge happens at trial end).
      return dayjs(trialEnd).format('YYYY-MM-DD');
    }
    if (!autoRenewal) {
      // Canceled subscription
      if (subscriptionEndDate && dayjs(subscriptionEndDate).isAfter(dayjs())) {
        // Access still valid (cancel-at-period-end): next billing = start of next period (Stripe period_end is that moment)
        return dayjs(subscriptionEndDate).format('YYYY-MM-DD');
      }
      // Access already ended (immediate cancel): first charge = today when user clicks Renew
      return dayjs().format('YYYY-MM-DD');
    }
    // Active subscription: next charge happens at current period end (not billing cycle anchor which is period start)
    if (subscriptionEndDate) {
      return dayjs(subscriptionEndDate).format('YYYY-MM-DD');
    }
    return anchor;
  })();

  // Projected amount: use subscribed counts (what they pay) for active/trialing; modal quantities for Renew/Start flow
  const seatCents = subscriptionPrices?.pricePerSeatCents ?? 0;
  const aircraftCents = subscriptionPrices?.pricePerAircraftCents ?? 0;
  const usersForProjection = !autoRenewal
    ? usersQuantity
    : (activePackage?.subscribedUsersCount ?? activePackage?.billedUsersCount ?? activeUsers);
  const aircraftForProjection = !autoRenewal
    ? aircraftQuantity
    : (activePackage?.subscribedAircraftCount ?? activePackage?.billedAircraftCount ?? activeAircraft);
  const fullMonthCents = usersForProjection * seatCents + (isOperator ? aircraftForProjection * aircraftCents : 0);
  const currency = subscriptionPrices?.currency ?? 'usd';

  // Trial ending in future: first charge is prorated (from trial end to end of month), then full monthly
  const trialEndAt = activePackage?.trialEndAt;
  const isTrialingWithFutureEnd = trialEndAt && dayjs(trialEndAt).isAfter(dayjs());

  // Discount applies for: (a) first-time subscriptions (Start flow), or (b) active subscription with discount
  const discountPercent = activePackage?.discountPercent ?? subscriptionPrices?.discountPercent;
  const discountDurationMonths = subscriptionPrices?.discountDurationMonths;
  const discountEndsAt = activePackage?.discountEndsAt ?? subscriptionPrices?.discountEndsAt;
  const hasDiscount = discountPercent != null && discountPercent > 0;
  const subscriptionHasActiveDiscount =
    hasDiscount &&
    autoRenewal &&
    discountEndsAt != null &&
    dayjs(discountEndsAt).isAfter(dayjs());
  const discountApplies =
    hasDiscount && !autoRenewal && neverHadSubscription; // Only first-time subscription gets the discount

  // When in Renew/Start flow or on trial with future end: prorate first period if not starting on 1st
  const {totalCents, isProrated, periodEndDate} = (() => {
    if (fullMonthCents <= 0) {
      return {totalCents: fullMonthCents, isProrated: false, periodEndDate: null as string | null};
    }
    // Skip proration only when autoRenewal AND not trialing (regular monthly billing on 1st)
    if (autoRenewal && !isTrialingWithFutureEnd) {
      return {totalCents: fullMonthCents, isProrated: false, periodEndDate: null as string | null};
    }
    if (!nextBillingDate) {
      return {totalCents: fullMonthCents, isProrated: false, periodEndDate: null as string | null};
    }
    const startDate = dayjs(nextBillingDate);
    const isFirstOfMonth = startDate.date() === 1;
    if (isFirstOfMonth) {
      return {totalCents: fullMonthCents, isProrated: false, periodEndDate: null as string | null};
    }
    const periodEnd = startDate.endOf('month').add(1, 'day');
    const daysInPeriod = Math.max(1, periodEnd.diff(startDate, 'day'));
    const daysInMonth = startDate.daysInMonth();
    const prorationFactor = Math.min(1, daysInPeriod / daysInMonth);
    const proratedCents = Math.round(fullMonthCents * prorationFactor);
    // Display valid until as last day of month (period end - 1 day) for consistency with renew start date
    const validUntilDate = periodEnd.subtract(1, 'day').format('DD.MM.YYYY');
    return {
      totalCents: proratedCents,
      isProrated: true,
      periodEndDate: validUntilDate,
    };
  })();

  // Full discounted months after the prorated first period (0 when the next month is already full price)
  const discountedMonthsForMessage = ((): number => {
    const totalMonths = discountDurationMonths ?? 0;
    if (totalMonths <= 0) return 0;
    if (!isProrated) return totalMonths;

    const billingStart = nextBillingDate ? dayjs(nextBillingDate) : null;
    if (!billingStart) return Math.max(0, totalMonths - 1);

    const firstFullMonthStart = billingStart.date() === 1
      ? billingStart
      : billingStart.endOf('month').add(1, 'day').startOf('day');

    if (discountEndsAt && !dayjs(discountEndsAt).isAfter(firstFullMonthStart, 'day')) {
      return 0;
    }

    if (discountEndsAt) {
      return Math.max(0, dayjs(discountEndsAt).diff(firstFullMonthStart, 'month'));
    }

    return Math.max(0, totalMonths - 1);
  })();

  // Apply discount to displayed amount when discount applies (new subscription) or active subscription has discount
  const displayCents =
    (discountApplies || subscriptionHasActiveDiscount) && totalCents > 0 && discountPercent != null
      ? Math.round(totalCents * (100 - discountPercent) / 100)
      : totalCents;
  const originalCents =
    (discountApplies || subscriptionHasActiveDiscount) && totalCents > 0 && discountPercent != null
      ? totalCents
      : null;

  const handleCancelSubscription = () => {
    if (activePackage?.companyTariffPackageId) {
      dispatch(cancelSubscription({companyTariffPackageId: activePackage.companyTariffPackageId}));
    }
  };

  const handleResumeSubscription = () => {
    if (!activePackage?.companyTariffPackageId) return;

    // If no cards, open add-card modal first; after successful add, we'll call resume
    if (paymentMethods.length === 0) {
      setPendingResumeAfterCardAdd(true);
      setIsPaymentMethodModalVisible(true);
      return;
    }

    dispatch(resumeSubscription({companyTariffPackageId: activePackage.companyTariffPackageId}));
  };

  const handlePaymentMethodSuccess = () => {
    // Dispatch resume first (before any state updates) when we came from "Start with no cards" flow
    if (pendingResumeAfterCardAdd && activePackage?.companyTariffPackageId) {
      dispatch(resumeSubscription({companyTariffPackageId: activePackage.companyTariffPackageId}));
    }
    setIsPaymentMethodModalVisible(false);
    setPendingResumeAfterCardAdd(false);
    if (!pendingResumeAfterCardAdd) {
      // Refresh payment methods (tariff is refreshed by attachPaymentMethod saga)
      const fetchPaymentMethods = async () => {
        try {
          const req = new BillingApiGetPaymentMethodsRequest();
          const res = await BillingApi.getPaymentMethods(req);
          if (res.getResultresponse()?.getIssuccessful()) {
            setPaymentMethods(res.getPaymentmethodsList().map(pm => ({id: pm.getPaymentmethodid()})));
          }
        } catch {
          // ignore
        }
      };
      fetchPaymentMethods();
      onSuccess?.();
    }
  };

  return (
    <>
      <CustomModal
        title={intl.formatMessage(ManageSubscriptionModalMessages.title)}
        open={open}
        onCancel={onClose}
        footer={null}
        width={500}
      >
        <ModalContent>
          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              closable
              onClose={() => setError(null)}
            />
          )}

          {activePackage && (
            <Section>
              <SectionTitle>{intl.formatMessage(ManageSubscriptionModalMessages.billingInfo)}</SectionTitle>
              <div style={{display: 'flex', flexDirection: 'column', rowGap: 8, fontSize: 14, color: '#666'}}>
                {nextBillingDate && (
                  <div>
                    <strong>{intl.formatMessage(ManageSubscriptionModalMessages.nextBillingDate)}:</strong>{' '}
                    {dayjs(nextBillingDate).format('DD.MM.YYYY')}
                  </div>
                )}
                {displayCents > 0 && (
                  <div style={{display: 'flex', flexDirection: 'column', rowGap: 4}}>
                    <div>
                      <strong>{intl.formatMessage(ManageSubscriptionModalMessages.projectedAmount)}:</strong>{' '}
                      {originalCents != null && originalCents > displayCents ? (
                        <>
                          <span style={{textDecoration: 'line-through', marginRight: 8}}>
                            {formatMoney(originalCents, currency)}
                          </span>
                          {discountApplies && hasDiscount && discountedMonthsForMessage > 0 ? (
                            isProrated && periodEndDate && fullMonthCents > 0
                              ? intl.formatMessage(ManageSubscriptionModalMessages.projectedAmountProratedWithDiscount, {
                                  amount: formatMoney(displayCents, currency),
                                  date: periodEndDate,
                                  discountedAmount: formatMoney(Math.round(fullMonthCents * (100 - (discountPercent ?? 0)) / 100), currency),
                                  months: discountedMonthsForMessage,
                                  fullAmount: formatMoney(fullMonthCents, currency),
                                })
                              : intl.formatMessage(ManageSubscriptionModalMessages.projectedAmountWithDiscount, {
                                  amount: formatMoney(displayCents, currency),
                                  months: discountedMonthsForMessage,
                                  fullAmount: formatMoney(fullMonthCents, currency),
                                })
                          ) : (
                            isProrated && periodEndDate && fullMonthCents > 0
                              ? intl.formatMessage(ManageSubscriptionModalMessages.projectedAmountProrated, {
                                  amount: formatMoney(displayCents, currency),
                                  date: periodEndDate,
                                  monthlyAmount: formatMoney(fullMonthCents, currency),
                                })
                              : intl.formatMessage(ManageSubscriptionModalMessages.projectedAmountFull, {
                                  amount: formatMoney(displayCents, currency),
                                })
                          )}
                        </>
                      ) : (
                        isProrated && periodEndDate && fullMonthCents > 0
                          ? intl.formatMessage(ManageSubscriptionModalMessages.projectedAmountProrated, {
                              amount: formatMoney(displayCents, currency),
                              date: periodEndDate,
                              monthlyAmount: formatMoney(fullMonthCents, currency),
                            })
                          : intl.formatMessage(ManageSubscriptionModalMessages.projectedAmountFull, {
                              amount: formatMoney(displayCents, currency),
                            })
                      )}
                    </div>
                    {subscriptionHasActiveDiscount && discountEndsAt && originalCents != null && (
                      <div style={{fontSize: 12, color: '#999'}}>
                        {intl.formatMessage(ManageSubscriptionModalMessages.discountUntilDate, {
                          percent: discountPercent ?? 0,
                          date: intl.formatDate(discountEndsAt, {month: 'short', year: 'numeric'}),
                          fullAmount: formatMoney(originalCents, currency),
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Section>
          )}

          {activePackage && (
            <Section>
              <SectionTitle>
                {autoRenewal
                  ? intl.formatMessage(ManageSubscriptionModalMessages.cancelSubscription)
                  : neverHadSubscription
                    ? intl.formatMessage(ManageSubscriptionModalMessages.startSubscription)
                    : intl.formatMessage(ManageSubscriptionModalMessages.renewSubscription)}
              </SectionTitle>
              {autoRenewal ? (
                <>
                  {subscriptionEndDate && (
                    <div style={{marginBottom: 12, fontSize: 14, color: '#666', display: 'flex', flexDirection: 'column', rowGap: 8}}>
                      <div>
                        {intl.formatMessage(ManageSubscriptionModalMessages.cancelMessage, {
                          date: dayjs(subscriptionEndDate).subtract(1, 'day').format('DD.MM.YYYY'),
                        })}
                      </div>
                      {subscriptionHasActiveDiscount && (
                        <div>
                          {intl.formatMessage(ManageSubscriptionModalMessages.cancelDiscountWarning)}
                        </div>
                      )}
                    </div>
                  )}
                  <CustomButton
                    danger
                    loading={cancelResumeLoading}
                    onClick={handleCancelSubscription}
                  >
                    {intl.formatMessage(ManageSubscriptionModalMessages.cancelSubscription)}
                  </CustomButton>
                </>
              ) : (
                <>
                  <div style={{display: 'flex', flexDirection: 'column', rowGap: 12, marginBottom: 12}}>
                    <div style={{display: 'flex', flexDirection: 'row', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start'}}>
                      <div style={{display: 'flex', flexDirection: 'column', rowGap: 4}}>
                        <label style={{fontSize: 14, fontWeight: 500, color: '#333'}}>
                          {intl.formatMessage(ManageSubscriptionModalMessages.seatsLabel)}
                        </label>
                        <InputNumber
                          min={billedUsers}
                          max={999}
                          value={usersQuantity}
                          onChange={(v) => setUsersQuantity(v ?? billedUsers)}
                          style={{width: 120}}
                        />
                      </div>
                      {isOperator && (
                        <div style={{display: 'flex', flexDirection: 'column', rowGap: 4}}>
                          <label style={{fontSize: 14, fontWeight: 500, color: '#333'}}>
                            {intl.formatMessage(ManageSubscriptionModalMessages.aircraftLabel)}
                          </label>
                          <InputNumber
                            min={billedAircraft}
                            max={999}
                            value={aircraftQuantity}
                            onChange={(v) => setAircraftQuantity(v ?? billedAircraft)}
                            style={{width: 120}}
                          />
                        </div>
                      )}
                    </div>
                    <div style={{fontSize: 14, color: '#666'}}>
                      {intl.formatMessage(ManageSubscriptionModalMessages.renewMessage, {
                        date: nextBillingDate
                          ? dayjs(nextBillingDate).format('DD.MM.YYYY')
                          : dayjs().format('DD.MM.YYYY'),
                        amount: displayCents > 0 ? formatMoney(displayCents, currency) : '—',
                      })}
                    </div>
                  </div>
                  <CustomButton
                    type="primary"
                    loading={cancelResumeLoading}
                    onClick={handleResumeSubscription}
                  >
                    {neverHadSubscription
                      ? intl.formatMessage(ManageSubscriptionModalMessages.startSubscription)
                      : intl.formatMessage(ManageSubscriptionModalMessages.renewSubscription)}
                  </CustomButton>
                </>
              )}
            </Section>
          )}
        </ModalContent>
      </CustomModal>
      <PaymentMethodModal
        isVisible={isPaymentMethodModalVisible}
        setIsVisible={(visible) => {
          setIsPaymentMethodModalVisible(visible);
          if (!visible) setPendingResumeAfterCardAdd(false);
        }}
        onSuccess={handlePaymentMethodSuccess}
      />
    </>
  );
};
